import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { isSecurityDashboardPdfPrintBrowserContext } from '@/lib/security-dashboard-pdf-injected-snapshot'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** True when `fetch` (or similar) was aborted via `AbortSignal`. */
export function isFetchAbortError(e: unknown): boolean {
  if (e == null || typeof e !== "object") return false
  const name = "name" in e && typeof (e as { name?: unknown }).name === "string" ? (e as { name: string }).name : ""
  return name === "AbortError"
}

/**
 * Convert seconds to minutes
 * @param seconds duration in seconds
 * @returns duration in minutes
 */
export function secondsToMinutes(seconds: number): number {
  return seconds / 60;
}

/**
 * Convert seconds to hours
 * @param seconds duration in seconds
 * @returns duration in hours
 */
export function secondsToHours(seconds: number): number {
  return seconds / 3600;
}

/**
 * Format a duration in whole seconds for SLA-style display (e.g. `2h 35m 55s`).
 * With one or more hours, always shows hours, minutes, and seconds. Below an hour,
 * shows minutes and seconds when both are non-zero, else the smallest non-zero unit.
 */
export function formatDurationSecondsCompact(totalSeconds: number): string {
  if (typeof totalSeconds !== "number" || !Number.isFinite(totalSeconds)) {
    return "0s"
  }
  const sTotal = Math.max(0, Math.round(totalSeconds))
  const h = Math.floor(sTotal / 3600)
  const m = Math.floor((sTotal % 3600) / 60)
  const s = sTotal % 60

  if (h > 0) {
    return `${h}h ${m}m ${s}s`
  }
  if (m > 0) {
    return s > 0 ? `${m}m ${s}s` : `${m}m`
  }
  return `${s}s`
}

/** Whole minutes (rounded) with `m` suffix — MTTx chart axis + hover when values are seconds. */
export function formatWholeMinutesFromSeconds(totalSeconds: number): string {
  if (typeof totalSeconds !== "number" || !Number.isFinite(totalSeconds)) {
    return "0m"
  }
  return `${Math.max(0, Math.round(totalSeconds / 60))}m`
}

/** Whole minutes (rounded) with `m` suffix — when wire values are already minutes (e.g. MTT* widget metrics, KPI setValue). */
export function formatWholeMinutes(totalMinutes: number): string {
  if (typeof totalMinutes !== "number" || !Number.isFinite(totalMinutes)) {
    return "0m"
  }
  return `${Math.max(0, Math.round(totalMinutes))}m`
}

/**
 * Get user's preferred timezone from session data or fallback to browser timezone
 * This function is now deprecated - use the useTimezone hook instead which gets timezone from session data
 * @param sessionData Session data object
 * @returns string User's preferred timezone
 */
export function getUserTimezoneFromSession(sessionData: { timezoneMetadata?: { userTimezone?: string } }): string {
  // First try to get from session timezone metadata
  if (sessionData?.timezoneMetadata?.userTimezone) {
    return sessionData.timezoneMetadata.userTimezone;
  }
  
  // Fallback to browser timezone
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Clear timezone cache for a specific session (call when session changes)
 * This function is now deprecated - no longer needed since we get timezone from session data
 */
export function clearTimezoneCache(sessionId?: string) {
  // No-op - no longer needed since we get timezone from session data
  console.warn('clearTimezoneCache is deprecated - timezone is now retrieved from session data');
}

/**
 * Convert UTC timestamp to user's preferred timezone
 * @param utcTimestamp UTC timestamp (string, Date, or number)
 * @param userTimezone User's preferred timezone
 * @returns Date object in user's timezone
 */
export function convertToUserTimezone(utcTimestamp: string | Date | number, userTimezone: string): Date {
  if (!utcTimestamp) {
    return new Date();
  }

  try {
    let utcDate: Date;
    
    // Handle different timestamp formats
    if (typeof utcTimestamp === 'number') {
      // Check if it's a Unix timestamp in milliseconds (13 digits) or seconds (10 digits)
      if (utcTimestamp > 9999999999) {
        // Unix timestamp in milliseconds
        utcDate = new Date(utcTimestamp);
      } else {
        // Unix timestamp in seconds, convert to milliseconds
        utcDate = new Date(utcTimestamp * 1000);
      }
    } else if (typeof utcTimestamp === 'string') {
      // Try to parse as ISO string first, then as Unix timestamp
      if (utcTimestamp.match(/^\d{10,13}$/)) {
        // Numeric string - treat as Unix timestamp
        const num = parseInt(utcTimestamp, 10);
        if (num > 9999999999) {
          utcDate = new Date(num);
        } else {
          utcDate = new Date(num * 1000);
        }
      } else {
        // Try as ISO string
        utcDate = new Date(utcTimestamp);
      }
    } else {
      // Already a Date object
      utcDate = utcTimestamp;
    }
    
    if (isNaN(utcDate.getTime())) {
      console.warn('Invalid UTC timestamp after conversion:', utcTimestamp, '->', utcDate);
      return new Date();
    }

    // Validate timezone - if empty or invalid, use browser default
    const validTimezone = userTimezone && userTimezone.trim() !== '' ? userTimezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Use Intl.DateTimeFormat to convert to user's timezone
    // This is more reliable than manual offset calculations
    const userDateString = utcDate.toLocaleString('en-US', { timeZone: validTimezone });
    return new Date(userDateString);
  } catch (error) {
    console.warn('Error converting to user timezone:', error, 'for timestamp:', utcTimestamp);
    return new Date(utcTimestamp);
  }
}

/**
 * Format date in user's preferred timezone
 * @param utcTimestamp UTC timestamp (string, Date, or number)
 * @param userTimezone User's preferred timezone
 * @param options Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string
 */
export function formatInUserTimezone(
  utcTimestamp: string | Date | number, 
  userTimezone: string, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }
): string {
  if (!utcTimestamp) return '';
  
  try {
    let utcDate: Date;
    
    // Handle different timestamp formats
    if (typeof utcTimestamp === 'number') {
      // Check if it's a Unix timestamp in milliseconds (13 digits) or seconds (10 digits)
      if (utcTimestamp > 9999999999) {
        // Unix timestamp in milliseconds
        utcDate = new Date(utcTimestamp);
      } else {
        // Unix timestamp in seconds, convert to milliseconds
        utcDate = new Date(utcTimestamp * 1000);
      }
    } else if (typeof utcTimestamp === 'string') {
      // Try to parse as ISO string first, then as Unix timestamp
      if (utcTimestamp.match(/^\d{10,13}$/)) {
        // Numeric string - treat as Unix timestamp
        const num = parseInt(utcTimestamp, 10);
        if (num > 9999999999) {
          utcDate = new Date(num);
        } else {
          utcDate = new Date(num * 1000);
        }
      } else {
        // Try as ISO string
        utcDate = new Date(utcTimestamp);
      }
    } else {
      // Already a Date object
      utcDate = utcTimestamp;
    }
    
    if (isNaN(utcDate.getTime())) {
      console.warn('Invalid UTC timestamp after conversion:', utcTimestamp, '->', utcDate);
      return '';
    }

    // Validate timezone - if empty or invalid, use browser default
    const validTimezone = userTimezone && userTimezone.trim() !== '' ? userTimezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: validTimezone
    }).format(utcDate);
  } catch (error) {
    console.warn('Error formatting date in user timezone:', error, 'for timestamp:', utcTimestamp);
    return new Date(utcTimestamp).toLocaleString('en-US', options);
  }
}

/**
 * Format date for display with timezone indicator
 * @param utcTimestamp UTC timestamp (string, Date, or number)
 * @param userTimezone User's preferred timezone
 * @param showTimezone Whether to show timezone indicator
 * @returns Formatted date string with optional timezone
 */
export function formatDateForDisplay(
  utcTimestamp: string | Date | number, 
  userTimezone: string, 
  showTimezone: boolean = false
): string {
  if (!utcTimestamp) return '';
  
  try {
    let utcDate: Date;
    
    // Handle different timestamp formats
    if (typeof utcTimestamp === 'number') {
      // Check if it's a Unix timestamp in milliseconds (13 digits) or seconds (10 digits)
      if (utcTimestamp > 9999999999) {
        // Unix timestamp in milliseconds
        utcDate = new Date(utcTimestamp);
      } else {
        // Unix timestamp in seconds, convert to milliseconds
        utcDate = new Date(utcTimestamp * 1000);
      }
    } else if (typeof utcTimestamp === 'string') {
      // Try to parse as ISO string first, then as Unix timestamp
      if (utcTimestamp.match(/^\d{10,13}$/)) {
        // Numeric string - treat as Unix timestamp
        const num = parseInt(utcTimestamp, 10);
        if (num > 9999999999) {
          utcDate = new Date(num);
        } else {
          utcDate = new Date(num * 1000);
        }
      } else {
        // Try as ISO string
        utcDate = new Date(utcTimestamp);
      }
    } else {
      // Already a Date object
      utcDate = utcTimestamp;
    }
    
    if (isNaN(utcDate.getTime())) {
      console.warn('Invalid UTC timestamp after conversion:', utcTimestamp, '->', utcDate);
      return '';
    }

    // Validate timezone - if empty or invalid, use browser default
    const validTimezone = userTimezone && userTimezone.trim() !== '' ? userTimezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formattedDate = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: validTimezone
    }).format(utcDate);

    if (showTimezone) {
      const timezoneAbbr = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
        timeZone: validTimezone
      }).format(utcDate).split(' ').pop() || '';
      
      return `${formattedDate} ${timezoneAbbr}`;
    }

    return formattedDate;
  } catch (error) {
    console.warn('Error formatting date for display:', error, 'for timestamp:', utcTimestamp);
    return new Date(utcTimestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }
}

/** Parse API or user timestamps into a Date for elapsed-time math (instant-based, no tz shift). */
function parseTimestampInputToDate(
  utcTimestamp: string | Date | number | null | undefined
): Date | null {
  if (utcTimestamp == null || utcTimestamp === "") {
    return null
  }

  try {
    let utcDate: Date

    if (typeof utcTimestamp === "number") {
      utcDate =
        utcTimestamp > 9999999999
          ? new Date(utcTimestamp)
          : new Date(utcTimestamp * 1000)
    } else if (typeof utcTimestamp === "string") {
      if (utcTimestamp.match(/^\d{10,13}$/)) {
        const num = Number.parseInt(utcTimestamp, 10)
        utcDate = num > 9999999999 ? new Date(num) : new Date(num * 1000)
      } else {
        utcDate = new Date(utcTimestamp)
      }
    } else {
      utcDate = utcTimestamp
    }

    if (Number.isNaN(utcDate.getTime())) {
      return null
    }
    return utcDate
  } catch {
    return null
  }
}

/**
 * Human-readable elapsed time from the event instant to `referenceNow` (e.g. "3 days ago").
 * Uses wall-clock instant difference; not affected by display timezone.
 */
export function formatAsTimeAgo(
  utcTimestamp: string | Date | number | null | undefined,
  referenceNow: Date
): string {
  const event = parseTimestampInputToDate(utcTimestamp)
  if (!event) {
    return ""
  }

  let diffInSeconds = Math.floor((referenceNow.getTime() - event.getTime()) / 1000)
  if (diffInSeconds < 0) {
    diffInSeconds = 0
  }

  if (diffInSeconds < 60) {
    return "just now"
  }
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`
  }
  if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} day${days !== 1 ? "s" : ""} ago`
  }
  if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000)
    return `${months} month${months !== 1 ? "s" : ""} ago`
  }
  const years = Math.floor(diffInSeconds / 31536000)
  return `${years} year${years !== 1 ? "s" : ""} ago`
}

/**
 * Compact count-up from an event instant to `referenceNow`, for live elapsed displays.
 * Examples: `0:42`, `2:05:31`, `3d 01:08:05`
 */
export function formatCountUpSince(
  utcTimestamp: string | Date | number | null | undefined,
  referenceNow: Date
): string {
  const event = parseTimestampInputToDate(utcTimestamp)
  if (!event) {
    return ""
  }

  let totalSec = Math.floor((referenceNow.getTime() - event.getTime()) / 1000)
  if (totalSec < 0) {
    totalSec = 0
  }

  const days = Math.floor(totalSec / 86400)
  let remainder = totalSec % 86400
  const h = Math.floor(remainder / 3600)
  remainder %= 3600
  const m = Math.floor(remainder / 60)
  const s = remainder % 60

  const hh = String(h).padStart(2, "0")
  const mm = String(m).padStart(2, "0")
  const ss = String(s).padStart(2, "0")
  const dayPrefix = days > 0 ? `${days}d ` : ""
  return `${dayPrefix}${hh}:${mm}:${ss}`
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago") as of the current client time.
 * @param utcTimestamp UTC timestamp (string, Date, or number)
 * @param _userTimezone Kept for backwards compatibility; elapsed time is instant-based and does not use this.
 */
export function getRelativeTime(
  utcTimestamp: string | Date | number,
  _userTimezone: string
): string {
  try {
    if (!utcTimestamp && utcTimestamp !== 0) return ""
    return formatAsTimeAgo(utcTimestamp, new Date())
  } catch (error) {
    console.warn("Error calculating relative time:", error, "for timestamp:", utcTimestamp)
    return ""
  }
}

/**
 * Transform MTTX API response to dashboard metrics format
 * @param mttxData MTTX metrics response from API
 * @returns transformed dashboard metrics
 * 
 * @example
 * // API Response:
 * {
 *   "success": true,
 *   "data": {
 *     "currentPeriod": {
 *       "mttd": 1800,      // 30 minutes in seconds
 *       "mtta": 7200,      // 2 hours in seconds
 *       "mttc": 28800,     // 8 hours in seconds
 *       "caseCount": 25,
 *       "detectedCases": 20,
 *       "assignedCases": 18,
 *       "closedCases": 15
 *     },
 *     "previousPeriod": {
 *       "mttd": 2700,      // 45 minutes in seconds
 *       "mtta": 9000,      // 2.5 hours in seconds
 *       "mttc": 36000      // 10 hours in seconds
 *     },
 *     "trends": {
 *       "mttdChange": -33.33,
 *       "mttaChange": -20.0,
 *       "mttcChange": -20.0
 *     }
 *   }
 * }
 * 
 * // Transformed Result:
 * {
 *   currentMTTD: 30,      // 30 minutes
 *   currentMTTA: 120,     // 2 hours in minutes
 *   currentMTTC: 8,       // 8 hours
 *   previousMTTD: 45,     // 45 minutes
 *   previousMTTA: 150,    // 2.5 hours in minutes
 *   previousMTTC: 10,     // 10 hours
 *   mttdChange: -33.33,
 *   mttaChange: -20.0,
 *   mttcChange: -20.0,
 *   isLive: true,
 *   lastUpdated: new Date()
 * }
 */
export function transformMttxResponse(mttxData: Record<string, unknown>) {
  if (!mttxData?.data) {
    return {
      currentMTTD: 0,
      currentMTTA: 0,
      currentMTTC: 0,
      previousMTTD: 0,
      previousMTTA: 0,
      previousMTTC: 0,
      mttdChange: 0,
      mttaChange: 0,
      mttcChange: 0,
      isLive: true,
      lastUpdated: new Date()
    };
  }

  const data = mttxData.data as Record<string, unknown>;
  const currentPeriod = data?.currentPeriod as Record<string, unknown>;
  const previousPeriod = data?.previousPeriod as Record<string, unknown>;
  const trends = data?.trends as Record<string, unknown>;

  return {
    currentMTTD: secondsToMinutes((currentPeriod?.mttd as number) || 0),
    currentMTTA: secondsToMinutes((currentPeriod?.mtta as number) || 0),
    currentMTTC: secondsToMinutes((currentPeriod?.mttc as number) || 0),  // Changed from secondsToHours
    previousMTTD: secondsToMinutes((previousPeriod?.mttd as number) || 0),
    previousMTTA: secondsToMinutes((previousPeriod?.mtta as number) || 0),
    previousMTTC: secondsToMinutes((previousPeriod?.mttc as number) || 0),  // Changed from secondsToHours
    mttdChange: (trends?.mttdChange as number) || 0,
    mttaChange: (trends?.mttaChange as number) || 0,
    mttcChange: (trends?.mttcChange as number) || 0,
    isLive: true,
    lastUpdated: new Date()
  };
}

/**
 * Debug utility to analyze timestamp format
 * @param timestamp The timestamp to analyze
 * @returns Analysis of the timestamp format
 */
export function analyzeTimestamp(timestamp: string | Date | number): {
  type: string;
  value: string | number | Date;
  isUnixMs: boolean;
  isUnixSec: boolean;
  isISO: boolean;
  isValid: boolean;
  parsedDate: Date | null;
  humanReadable: string;
} {
  const result = {
    type: typeof timestamp,
    value: timestamp,
    isUnixMs: false,
    isUnixSec: false,
    isISO: false,
    isValid: false,
    parsedDate: null as Date | null,
    humanReadable: ''
  };

  try {
    if (typeof timestamp === 'number') {
      if (timestamp > 9999999999) {
        result.isUnixMs = true;
        result.parsedDate = new Date(timestamp);
      } else {
        result.isUnixSec = true;
        result.parsedDate = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === 'string') {
      if (timestamp.match(/^\d{10,13}$/)) {
        const num = parseInt(timestamp, 10);
        if (num > 9999999999) {
          result.isUnixMs = true;
          result.parsedDate = new Date(num);
        } else {
          result.isUnixSec = true;
          result.parsedDate = new Date(num * 1000);
        }
      } else {
        result.isISO = true;
        result.parsedDate = new Date(timestamp);
      }
    } else {
      result.parsedDate = timestamp;
    }

    if (result.parsedDate && !isNaN(result.parsedDate.getTime())) {
      result.isValid = true;
      result.humanReadable = result.parsedDate.toISOString();
    }
  } catch (error) {
    console.error('Error analyzing timestamp:', error);
  }

  return result;
}

/**
 * Safe timestamp parsing with detailed logging
 * @param timestamp The timestamp to parse
 * @param context Context for logging (e.g., "session.createdAt")
 * @returns Parsed Date object or null if invalid
 */
export function safeParseTimestamp(timestamp: string | Date | number, context: string = 'unknown'): Date | null {
  if (!timestamp) {
    console.warn(` [TIMESTAMP] Empty timestamp in context: ${context}`);
    return null;
  }

  const analysis = analyzeTimestamp(timestamp);
  
  if (!analysis.isValid) {
    console.warn(` [TIMESTAMP] Invalid timestamp in context: ${context}`, {
      original: timestamp,
      type: analysis.type,
      analysis
    });
    return null;
  }

  console.log(` [TIMESTAMP] Successfully parsed timestamp in context: ${context}`, {
    original: timestamp,
    parsed: analysis.humanReadable,
    type: analysis.type,
    isUnixMs: analysis.isUnixMs,
    isUnixSec: analysis.isUnixSec,
    isISO: analysis.isISO
  });

  return analysis.parsedDate;
}

/**
 * Format time remaining until expiration in a human-readable format
 * @param expiresAt Unix timestamp in milliseconds
 * @returns Formatted duration string
 */
export function formatTimeRemaining(expiresAt: number): string {
  const now = new Date().getTime();
  const timeRemaining = expiresAt - now;
  
  if (timeRemaining <= 0) return 'Expired';
  
  const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) {
    return `In ${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `In ${hours}h ${minutes}m`;
  } else {
    return `In ${minutes}m`;
  }
}

// Token management utilities
export interface TokenInfo {
  isValid: boolean;
  expiresAt: Date | null;
  timeUntilExpiry: number | null;
  isExpiringSoon: boolean;
  payload: any | null;
}

/**
 * Decode and validate JWT token
 */
export function decodeAndValidateToken(token: string): TokenInfo {
  try {
    if (!token || typeof token !== 'string') {
      return {
        isValid: false,
        expiresAt: null,
        timeUntilExpiry: null,
        isExpiringSoon: false,
        payload: null
      };
    }

    // Split token and get payload
    const parts = token.split('.');
    if (parts.length !== 3) {
      return {
        isValid: false,
        expiresAt: null,
        timeUntilExpiry: null,
        isExpiringSoon: false,
        payload: null
      };
    }

    // Decode payload
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Date.now() / 1000; // Convert to seconds
    const expirationTime = payload.exp;
    
    if (!expirationTime) {
      return {
        isValid: false,
        expiresAt: null,
        timeUntilExpiry: null,
        isExpiringSoon: false,
        payload
      };
    }

    const timeUntilExpiry = expirationTime - currentTime;
    const expiresAt = new Date(expirationTime * 1000);
    const isExpiringSoon = timeUntilExpiry < 300; // 5 minutes

    return {
      isValid: timeUntilExpiry > 0,
      expiresAt,
      timeUntilExpiry,
      isExpiringSoon,
      payload
    };
  } catch (error) {
    console.warn('Token validation error:', error);
    return {
      isValid: false,
      expiresAt: null,
      timeUntilExpiry: null,
      isExpiringSoon: false,
      payload: null
    };
  }
}

/**
 * Get current auth token from sessionStorage (preferred) or localStorage (fallback)
 * This matches the storage strategy used in lib/auth.tsx
 */
export function getCurrentAuthToken(): string | null {
  try {
    // Check sessionStorage first (preferred), then localStorage (fallback)
    const authTokenData = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : null)
      || (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
    
    if (!authTokenData) return null;

    const parsed = JSON.parse(authTokenData);
    return parsed.access_token || null;
  } catch (error) {
    console.warn('Error getting auth token:', error);
    return null;
  }
}

/**
 * Check if current token is valid and not expiring soon
 */
export function isTokenValid(): boolean {
  const token = getCurrentAuthToken();
  if (!token) return false;

  const tokenInfo = decodeAndValidateToken(token);
  return tokenInfo.isValid && !tokenInfo.isExpiringSoon;
}

/**
 * Get token expiration info
 */
export function getTokenExpirationInfo(): TokenInfo {
  const token = getCurrentAuthToken();
  if (!token) {
    return {
      isValid: false,
      expiresAt: null,
      timeUntilExpiry: null,
      isExpiringSoon: false,
      payload: null
    };
  }

  return decodeAndValidateToken(token);
}

/**
 * Format time until expiry for display
 */
export function formatTimeUntilExpiry(seconds: number): string {
  if (seconds <= 0) return 'Expired';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Clear all session data and redirect to login
 * Used when sessions are cleaned up, revoked, or lost
 */
export type SessionCleanupReason = 'cleaned_up' | 'revoked' | 'expired' | 'invalid' | 'session_creation_failed';

export const SESSION_CLEANUP_MESSAGES: Record<SessionCleanupReason, string> = {
  cleaned_up: 'Your session has been cleaned up. Please log in again to continue.',
  revoked: 'Your session has been revoked for security reasons. Please log in again.',
  expired: 'Your session has expired. Please log in again to continue.',
  invalid: 'Your session is no longer valid. Please log in again to continue.',
  session_creation_failed: "We couldn't create your session. Please sign in again.",
}

export function clearAuthCookies(): void {
  if (typeof document === 'undefined') return;

  const host = window.location.hostname;
  const domainParts = host.split('.').filter(Boolean);
  const domainCandidates = new Set<string>(['']);

  // Expire cookies at current host and parent domains.
  for (let i = 0; i < domainParts.length - 1; i++) {
    domainCandidates.add(`.${domainParts.slice(i).join('.')}`);
  }

  const cookieNames = document.cookie
    .split(';')
    .map(entry => entry.split('=')[0]?.trim())
    .filter(Boolean) as string[];

  cookieNames.forEach((name) => {
    domainCandidates.forEach((domain) => {
      const domainAttr = domain ? `;domain=${domain}` : '';
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttr};SameSite=Lax`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttr};Secure;SameSite=None`;
    });
  });
}

export function clearSessionAndRedirectToLogin(reason: SessionCleanupReason = 'invalid'): void {
  if (typeof window === 'undefined') return
  console.log(`🧹 [SESSION CLEANUP] Full sign-out — reason: ${reason}`)
  void import('@/lib/force-full-sign-out').then(({ forceFullSignOut }) =>
    forceFullSignOut({ reason })
  )
}

/**
 * Get session message if one was set during cleanup
 */
export function getSessionMessage(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('sessionMessage');
}

/**
 * Clear session message after displaying it
 */
export function clearSessionMessage(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('sessionMessage');
}

// Utility to prevent repetitive console logging
class LogManager {
  private loggedMessages = new Map<string, number>();
  private readonly maxLogsPerMessage = 3; // Only log the same message 3 times
  private readonly resetInterval = 60000; // Reset counters every minute

  constructor() {
    // Reset counters periodically
    setInterval(() => {
      this.loggedMessages.clear();
    }, this.resetInterval);
  }

  log(message: string, ...args: any[]) {
    const count = this.loggedMessages.get(message) || 0;
    
    if (count < this.maxLogsPerMessage) {
      console.log(message, ...args);
      this.loggedMessages.set(message, count + 1);
    } else if (count === this.maxLogsPerMessage) {
      console.log(` [LOG-MANAGER] Suppressing repetitive log: "${message}" (logged ${count} times)`);
      this.loggedMessages.set(message, count + 1);
    }
  }

  error(message: string, ...args: any[]) {
    const count = this.loggedMessages.get(message) || 0;
    
    if (count < this.maxLogsPerMessage) {
      console.error(message, ...args);
      this.loggedMessages.set(message, count + 1);
    } else if (count === this.maxLogsPerMessage) {
      console.error(` [LOG-MANAGER] Suppressing repetitive error: "${message}" (logged ${count} times)`);
      this.loggedMessages.set(message, count + 1);
    }
  }

  warn(message: string, ...args: any[]) {
    const count = this.loggedMessages.get(message) || 0;
    
    if (count < this.maxLogsPerMessage) {
      console.warn(message, ...args);
      this.loggedMessages.set(message, count + 1);
    } else if (count === this.maxLogsPerMessage) {
      console.warn(` [LOG-MANAGER] Suppressing repetitive warning: "${message}" (logged ${count} times)`);
      this.loggedMessages.set(message, count + 1);
    }
  }
}

// Create a singleton instance
export const logManager = new LogManager();
